/**
 * Typed wrappers for the custom (non-CRUD) super-admin Conference endpoints:
 * archive/restore, the company-grant sub-resource, the assigned-users list,
 * bulk (un)assignment, and the per-conference toolkit.
 *
 * Standard Conference CRUD (list/get/create/update) goes through Refine's
 * dataProvider; everything cross-tenant or verb-shaped lives here.
 *
 * Backend reality (viq-backend/functions-superadmin):
 *  - GET .../companies and .../users return `{ companies }` / `{ users }`
 *    wrappers of the FULL entity rows (not slim projections), so we unwrap
 *    to `Company[]` / `UserWithAssignments[]`.
 *  - Grants + bulk assignments carry `targetCompanyId` (the mutated tenant).
 *  - Bulk assignment pairs are `{ userOid, conferenceId }` (shared
 *    BulkAssignmentInputSchema).
 */
import type { Company, ToolkitResponse, ToolkitType } from '@viq-admin/types';
import { superadminFetch } from './dataProvider';
import type { UserWithAssignments } from './users';

// ── Archive / restore ───────────────────────────────────────────────

/** POST /v1/superadmin/conferences/{id}/archive — no body. */
export async function archiveConference(id: string): Promise<void> {
  await superadminFetch<void>(`/v1/superadmin/conferences/${id}/archive`, {
    method: 'POST',
  });
}

/** POST /v1/superadmin/conferences/{id}/restore — no body. */
export async function restoreConference(id: string): Promise<void> {
  await superadminFetch<void>(`/v1/superadmin/conferences/${id}/restore`, {
    method: 'POST',
  });
}

// ── Company grants ──────────────────────────────────────────────────

/** GET /v1/superadmin/conferences/{id}/companies → grantee companies. */
export async function listConferenceCompanies(
  confId: string,
): Promise<Company[]> {
  const body = await superadminFetch<{ companies: Company[] }>(
    `/v1/superadmin/conferences/${confId}/companies`,
  );
  return body.companies;
}

/** POST /v1/superadmin/conferences/{id}/companies — body `{ targetCompanyId }`. */
export async function grantCompanyAccess(
  confId: string,
  targetCompanyId: string,
): Promise<void> {
  await superadminFetch<void>(
    `/v1/superadmin/conferences/${confId}/companies`,
    { method: 'POST', body: JSON.stringify({ targetCompanyId }) },
  );
}

/** DELETE /v1/superadmin/conferences/{id}/companies/{companyId}. */
export async function revokeCompanyAccess(
  confId: string,
  companyId: string,
): Promise<void> {
  await superadminFetch<void>(
    `/v1/superadmin/conferences/${confId}/companies/${companyId}`,
    { method: 'DELETE' },
  );
}

// ── Assigned users + bulk (un)assignment ────────────────────────────

/** GET /v1/superadmin/conferences/{id}/users → all assigned reps (cross-tenant). */
export async function listConferenceUsers(
  confId: string,
): Promise<UserWithAssignments[]> {
  const body = await superadminFetch<{ users: UserWithAssignments[] }>(
    `/v1/superadmin/conferences/${confId}/users`,
  );
  return body.users;
}

/**
 * POST /v1/superadmin/assignments — bulk-assign `userOids` to this conference
 * within `targetCompanyId`. Pairs are `{ userOid, conferenceId }`.
 */
export async function bulkAssign(
  confId: string,
  targetCompanyId: string,
  userOids: string[],
): Promise<{ requested: number; inserted: number }> {
  return superadminFetch<{ requested: number; inserted: number }>(
    `/v1/superadmin/assignments`,
    {
      method: 'POST',
      body: JSON.stringify({
        targetCompanyId,
        pairs: userOids.map((userOid) => ({ userOid, conferenceId: confId })),
      }),
    },
  );
}

/** DELETE /v1/superadmin/assignments — bulk-unassign the same pairs. */
export async function bulkUnassign(
  confId: string,
  targetCompanyId: string,
  userOids: string[],
): Promise<{ requested: number; deleted: number }> {
  return superadminFetch<{ requested: number; deleted: number }>(
    `/v1/superadmin/assignments`,
    {
      method: 'DELETE',
      body: JSON.stringify({
        targetCompanyId,
        pairs: userOids.map((userOid) => ({ userOid, conferenceId: confId })),
      }),
    },
  );
}

// ── Per-conference toolkit ──────────────────────────────────────────

export interface ToolkitPutBody {
  toolkitType: ToolkitType;
  config: Record<string, unknown>;
  secrets?: Record<string, string>;
}

/**
 * GET /v1/superadmin/conferences/{id}/toolkit.
 * The backend 404s when no toolkit is configured; we translate that to `null`.
 */
export async function getToolkit(
  confId: string,
): Promise<ToolkitResponse | null> {
  try {
    return await superadminFetch<ToolkitResponse>(
      `/v1/superadmin/conferences/${confId}/toolkit`,
    );
  } catch (err) {
    if ((err as { statusCode?: number })?.statusCode === 404) return null;
    throw err;
  }
}

/** PUT /v1/superadmin/conferences/{id}/toolkit. */
export async function putToolkit(
  confId: string,
  body: ToolkitPutBody,
): Promise<void> {
  await superadminFetch<void>(
    `/v1/superadmin/conferences/${confId}/toolkit`,
    { method: 'PUT', body: JSON.stringify(body) },
  );
}

/** DELETE /v1/superadmin/conferences/{id}/toolkit. */
export async function deleteToolkit(confId: string): Promise<void> {
  await superadminFetch<void>(
    `/v1/superadmin/conferences/${confId}/toolkit`,
    { method: 'DELETE' },
  );
}
